/**
 * User profile API calls.
 *
 * Mirrors the legacy AngularJS $profileService ($resource-based) and related endpoints.
 */
import apiClient from './client';
import { buildAuditSearchUrl, type AuditSearchParams } from './auditSearch';
import { brewauthAdmin } from '@/api/brewauthAdminClient';
import type { CredentialRemoveResult, CredentialRenameResult } from '@/api/brewauthAdminClient';
import type { Document, UserProfile, SkipLimitSearchResponse, SearchParams } from '@/types';
import type { FolderEntry } from '@/api/folders';

// ── Profile audits ────────────────────────────────────────────────────

export type ProfileAuditType =
  | 'LOGIN'
  | 'FAILED_LOGIN'
  | 'LOGOUT'
  | 'CREATE'
  | 'UPDATE'
  | 'MERGE'
  | 'DELETE';

/**
 * brewauth's process identity, written to `attributes.actor` (and
 * `metadata.createdBy`) on every account change no person made: the lockout
 * escalation, the inactivity timer, the boot-time seed and credential
 * migration, the issuer self-healer, and a lifecycle job with no initiator.
 * A verbatim copy of `profileaudit.SystemActorDN`
 * (brewauth internal/profileaudit/account.go), which is a wire contract:
 * that side never changes it, so an equality facet on it
 * (`^attributes.actor=<this>`) is how the audit search finds
 * system-initiated rows across every verb.
 */
export const BREWAUTH_SYSTEM_ACTOR_DN =
  'CN=BREWAUTH,OU=SYSTEM,OU=AUTH,OU=BREW,O=N-ASK,L=FAIRFAX,ST=VA,C=US';

/** Attributes returned on every profile-audit row. LOGIN events also carry
 *  an `X-Forwarded-For` array of IP strings; FAILED_LOGIN events carry an
 *  `outcome` string (e.g. "invalid" / "locked" / "disabled" / "expired").
 *
 *  brewauth's account-management rows (jorigin/brewauth#89, #93 — CREATE,
 *  UPDATE, and brewauth's own MERGE / DELETE) carry `change` (what changed:
 *  "disabled", "password", "credential_renamed", "delete_requested", …),
 *  `method` (the path: "admin", "reset", "inactivity",
 *  "lockout_escalation", …), `actor` (the DN of who did it — an admin, the
 *  holder, or `BREWAUTH_SYSTEM_ACTOR_DN`), an `outcome` that is "success"
 *  or the reason a change was refused, and `previousDN` on a rename. A
 *  `credential_renamed` row also carries `issuer` / `previousIssuer`
 *  (jorigin/brewauth#28) — rows written before that have neither, and an
 *  unknown stored issuer is the empty string, not an absent key. On
 *  those rows the SUBJECT is the account that changed, not the actor. */
export interface ProfileAuditAttributes {
  dn?: string;
  'X-Forwarded-For'?: string[];
  outcome?: string;
  change?: string;
  method?: string;
  actor?: string;
  previousDN?: string;
  issuer?: string;
  previousIssuer?: string;
  [k: string]: unknown;
}

export interface ProfileAuditEntry {
  subject?: string;
  verb?: ProfileAuditType;
  attributes?: ProfileAuditAttributes;
  timestamp?: string;
  object?: string;
}

/**
 * Search the profile audit log.
 *
 *   GET api/profiles/audits/search?^auditType=LOGIN&text=*&limit=20&skip=0&sort=-timestamp
 *
 * The leading `^` on `auditType` is a legacy server predicate marker and
 * must not be percent-encoded, so we build the URL by hand rather than
 * letting axios serialize a params object — same pattern as `searchTaskAudits`.
 *
 * Omit `auditType` to fetch all audit verbs at once (the predicate is left
 * off entirely so the server returns every type).
 */
export async function searchProfileAudits(
  params: AuditSearchParams<ProfileAuditType> = {},
): Promise<SkipLimitSearchResponse<ProfileAuditEntry>> {
  const url = buildAuditSearchUrl('api/profiles/audits/search', params);
  const res = await apiClient.get<SkipLimitSearchResponse<ProfileAuditEntry>>(url);
  return res.data;
}

// ── Profiles ──────────────────────────────────────────────────────────

/** Get the current authenticated user's profile. */
export async function getSelf(): Promise<UserProfile> {
  const res = await apiClient.get<UserProfile>('api/profiles/self');
  return res.data;
}

/** Get a specific user's profile by ID. */
export async function getProfile(userId: string): Promise<UserProfile> {
  const res = await apiClient.get<UserProfile>(`api/profiles/${userId}`);
  return res.data;
}

/** Update a user profile. Issued as `{ action: "update", options: <fields> }`. */
export async function updateProfile(userId: string, data: Partial<UserProfile>): Promise<UserProfile> {
  const res = await apiClient.post<UserProfile>(`api/profiles/${userId}`, {
    action: 'update',
    options: data,
  });
  return res.data;
}

/** Get total profile count. */
export async function getProfileCount(): Promise<number> {
  const res = await apiClient.get<number | string>('api/profiles/count');
  return Number(res.data) || 0;
}

/** One bucket from `api/profiles/activity`. */
export interface UserActivityBucket {
  ts: number;
  at?: string;
  count: number;
  /** Profile IDs of users active during this bucket. */
  subjects: string[];
}

/** Response from `api/profiles/activity`. */
export interface UserActivityResponse {
  duration: string;
  epoch: string;
  epoch_ms?: number;
  values: UserActivityBucket[];
  units: string;
  /** Hydrated profiles for any subject referenced by `values[].subjects`. */
  $profiles?: UserProfile[];
}

/** Get the per-bucket distinct user activity histogram. */
export async function getUserActivity(
  params: { days?: number; samples?: number } = {},
): Promise<UserActivityResponse> {
  const { days = 7, samples = days === 1 ? 24 : days } = params;
  const res = await apiClient.get<UserActivityResponse>('api/profiles/activity', {
    params: { days, samples },
  });
  return res.data;
}

/**
 * Search profiles.
 *
 * Example: `api/profiles/search?limit=10&skip=0&sort=metadata.label.value&text=*`
 * Response shape: `{ total, data, limit, skip }`.
 */
/**
 * Profile search params: {@link SearchParams} plus the admin
 * server-side filters (#2071). `status` matches the admin Profiles
 * tabs 1:1 (omit for "All"); `system: false` excludes SYSTEM-group
 * members (omit to include everyone). `status: 'deleted'` is the
 * tombstone-visibility exception: every other status (including omitting
 * `status` for "All") implicitly excludes tombstoned profiles server-side,
 * so `deleted` is the only way to see them.
 */
export interface ProfileSearchParams extends SearchParams {
  status?: 'online' | 'inactive' | 'disabled' | 'unconfirmed' | 'deleted';
  system?: boolean;
}

export async function searchProfiles(params: ProfileSearchParams): Promise<SkipLimitSearchResponse<UserProfile>> {
  const res = await apiClient.get<SkipLimitSearchResponse<UserProfile>>('api/profiles/search', { params });
  return res.data;
}

/** Get all profile tags. */
export async function getProfileTags(): Promise<string[]> {
  const res = await apiClient.get<string[]>('api/profiles/tags');
  return res.data;
}

/**
 * Search profile tags by query string for autocomplete.
 * Server returns `{ [tag]: count }` (legacy) or an array of tag strings.
 * Returns suggestions as `{ tag, count }` ordered by descending count.
 */
export interface ProfileTagSuggestion {
  tag: string;
  count: number;
}

export async function searchProfileTags(query: string): Promise<ProfileTagSuggestion[]> {
  const res = await apiClient.get<string[] | Record<string, number>>('api/profiles/tags', {
    params: { query },
  });
  const data = res.data;
  if (Array.isArray(data)) return data.map((tag) => ({ tag, count: 0 }));
  return Object.entries(data || {})
    .map(([tag, count]) => ({ tag, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);
}

/**
 * POST a tags update for a profile. Always sends the complete tags array:
 * the server's `update` merge replaces array values wholesale, but stores a
 * sparse-indexed object payload (`{"0": "abc"}`) verbatim when
 * `metadata.tags` doesn't exist yet, corrupting it into an object. Full
 * arrays are safe in every case and also repair previously corrupted
 * profiles on the next save.
 */
export async function updateProfileTags(
  profileId: string,
  tags: string[],
): Promise<UserProfile> {
  const res = await apiClient.post<UserProfile>(`api/profiles/${profileId}`, {
    action: 'update',
    options: { metadata: { tags } },
  });
  return res.data;
}

/** Get user's folders. */
export async function getUserFolders(userId: string): Promise<unknown[]> {
  const res = await apiClient.get(`api/profiles/${userId}/folders`);
  return res.data;
}

/** Get user's models. */
export async function getUserModels(userId: string): Promise<unknown[]> {
  const res = await apiClient.get(`api/profiles/${userId}/models`);
  return res.data;
}

/**
 * Top-level model folders for a profile (My Models, My Shared Models,
 * Others Shared, …). Returned by `GET /api/profiles/:id/folders/$all/folders`.
 */
export async function getProfileFolders(userId: string): Promise<FolderEntry[]> {
  const res = await apiClient.get<FolderEntry[]>(
    `api/profiles/${userId}/folders/$all/folders`,
  );
  return res.data;
}

/**
 * Search documents owned by a profile.
 *
 * Hits `GET api/profiles/:userId/documents/search` and returns the standard
 * skip/limit envelope. `text` defaults to `*` so an empty query lists everything.
 */
export async function searchProfileDocuments(
  userId: string,
  params: SearchParams = {},
): Promise<SkipLimitSearchResponse<Document>> {
  const { text, ...rest } = params;
  const res = await apiClient.get<SkipLimitSearchResponse<Document>>(
    `api/profiles/${userId}/documents/search`,
    { params: { text: text?.trim() ? text : '*', ...rest } },
  );
  return res.data;
}

/**
 * Get the models the user saved or ran recently, the latest first: Discover's
 * "Continue editing" (jorigin/brewlytics#2208; not the ones they only opened).
 */
export async function getRecentItems(): Promise<unknown[]> {
  const res = await apiClient.get('api/profiles/self/recent');
  return res.data;
}

/**
 * Get the user's favorites they may still read, newest first. `type` is
 * `model`, `document`, `functional` or `*` (every kind, the default); `limit`
 * caps how many come back.
 */
export async function getFavorites(type?: string, limit?: number): Promise<unknown[]> {
  const params: Record<string, string | number> = {};
  if (type) params.type = type;
  if (limit != null) params.limit = limit;
  const res = await apiClient.get('api/profiles/self/favorites', {
    params: Object.keys(params).length ? params : undefined,
  });
  return res.data;
}

/** Get user avatar URL. */
export function getAvatarUrl(userId: string): string {
  return `api/profiles/${userId}/avatar`;
}

/** Upload (or replace) a user's avatar image. */
export async function uploadAvatar(userId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await apiClient.post(`api/profiles/${userId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Expire a BASIC credential's password (admin). Delegates to brewauth, which
 * now owns credential lifecycle (jorigin/brewauth#10/#35 — Java's equivalent
 * action answers 410 Gone).
 *
 * Marks the credential so the next login with it is forced through the
 * "your password has expired → reset email" flow (the same path a naturally
 * aged-out password hits). `credential` is the credential's DN string, e.g.
 * `credentialDnString(cred)`.
 */
export async function expireCredentialPassword(userId: string, credential: string): Promise<void> {
  await brewauthAdmin.expireCredential(userId, credential);
}

/**
 * Remove a credential from a profile (admin). Delegates to brewauth, which
 * now owns credential lifecycle (jorigin/brewauth#10/#35 — Java's equivalent
 * action answers 410 Gone) and resolves the migration target itself:
 *
 *  - exactly one credential would remain -> removed immediately, `targetDn`
 *    unused.
 *  - more than one would remain -> rejects with a 400 whose thrown error
 *    carries `candidates` (`CredentialCandidate[]`, from `@/api/brewauthAdminClient`);
 *    retry with the admin's chosen `targetDn`.
 *  - this is the last credential -> rejects with a 409 (thrown error's
 *    `status`); callers should offer profile deletion instead of hiding the
 *    control.
 *
 * Removal itself is synchronous; the brewlytics/brewdb resource migration it
 * enqueues is not — it completes within a worker tick, so callers should not
 * imply it has already finished. `credential`/`targetDn` are DN strings, e.g.
 * `credentialDnString(cred)`.
 *
 * Returns the server's `CredentialRemoveResult` rather than swallowing it:
 * its `warning` field is set on the one path where the removal committed but
 * the migration failed to enqueue, and that needs to reach the admin — a
 * caller that discarded the body would have no way to tell that case apart
 * from an ordinary success.
 */
export async function removeCredential(
  userId: string,
  credential: string,
  targetDn?: string,
): Promise<CredentialRemoveResult> {
  return brewauthAdmin.removeCredential(userId, credential, targetDn);
}

/**
 * Rewrite a PKI credential's subject and issuer DN strings (admin).
 * Delegates to brewauth, which owns credential mutation (jorigin/brewauth#35):
 * it re-signs the stored entry and rejects a target already assigned to
 * another profile with 409 Conflict. `credential` is the current subject DN
 * string, e.g. `credentialDnString(cred)`.
 *
 * The rename itself is synchronous; the brewlytics/brewdb resource
 * migration it enqueues is not — it lands within a worker tick, so callers
 * should not imply it has already finished.
 *
 * Returns the server's `CredentialRenameResult` rather than swallowing it:
 * its `warning` field is set on the one path where the rename committed but
 * the migration failed to enqueue, and that needs to reach the admin — a
 * caller that discarded the body would have no way to tell that case apart
 * from an ordinary success.
 */
export async function updatePkiCredential(
  userId: string,
  credential: string,
  subject: string,
  issuer: string,
): Promise<CredentialRenameResult> {
  return brewauthAdmin.updateCredentialDn(userId, credential, { subjectDn: subject, issuerDn: issuer });
}

/** Activate a user account (admin). Delegates to brewauth, which owns
 *  profile activation; an optional `clientIds` list also grants app access
 *  for each app as part of the same call. */
export async function activateProfile(userId: string, clientIds?: string[]): Promise<void> {
  await brewauthAdmin.activateProfile(userId, clientIds);
}

/**
 * Re-send the signup email-verification message for a still-unconfirmed
 * account (admin). Delegates to brewauth, which owns verification
 * (jorigin/brewauth#23).
 *
 * The admin-assist counterpart to the user's own `/api/verify/resend`, for
 * someone who cannot complete that themselves — a code that expired while
 * they were away, or mail that never arrived. Re-issuing invalidates any
 * outstanding code.
 *
 * Rejects rather than reporting a silent success when the account has
 * nothing to verify (409) or verification is switched off server-side
 * (403); both carry the server's own wording on the thrown error's
 * `serverMessage`.
 */
export async function resendVerificationEmail(userId: string): Promise<void> {
  await brewauthAdmin.adminResendVerifyEmail(userId);
}

/** Disable a user account (admin). Delegates to brewauth. */
export async function disableProfile(userId: string): Promise<void> {
  await brewauthAdmin.disableProfile(userId);
}

/** Enable a user account (admin). Delegates to brewauth. */
export async function enableProfile(userId: string): Promise<void> {
  await brewauthAdmin.enableProfile(userId);
}

/**
 * Java's resource counts for a profile (`GET api/profiles/:id/resources`).
 * Java's response also carries `groups` and `memberships`; gui-react no longer
 * reads them -- those two are brewauth's (`getProfileGroupCounts`,
 * jorigin/brewauth#120 P3-6), so Java may drop them.
 */
export interface ProfileResources {
  models: number;
  documents: number;
  tasks: number;
}

/** Count Java-owned resources for a profile (used before deletion). */
export async function getProfileResources(userId: string): Promise<ProfileResources> {
  const res = await apiClient.get<ProfileResources>(`api/profiles/${userId}/resources`);
  return res.data;
}

/**
 * Start an orchestrated account-deletion job (brewauth). If the profile owns
 * resources, `assigneeId` must be supplied so ownership is reassigned to the
 * given profile; otherwise the job runs with no-transfer. Async: returns the
 * job id, not a completed deletion.
 */
export async function deleteProfile(userId: string, assigneeId?: string): Promise<{ job_id: string }> {
  return brewauthAdmin.createDeletionJob(userId, assigneeId ? { assigneeId } : { noTransfer: true });
}

/** Partitioned result of a merge fan-out: one entry per source, sorted into
 *  the job that started and the ones that didn't. */
export interface MergeProfilesResult {
  started: { sourceId: string; job_id: string }[];
  failed: { sourceId: string; error: string }[];
}

/**
 * Merge one or more source profiles into a base profile (admin).
 *
 * Delegates to brewauth, which owns account merge as an orchestrated job:
 * one `createMergeJob` per source, fanned out in parallel. Async: returns
 * the job ids, not completed merges.
 *
 * Uses `Promise.allSettled` rather than `Promise.all` so one source
 * rejecting (e.g. a merge already pending for it) doesn't hide the jobs
 * that *did* get created for the other sources — callers get back the
 * full partition instead of a single rejection.
 */
export async function mergeProfiles(
  baseId: string,
  sourceIds: string[],
): Promise<MergeProfilesResult> {
  const results = await Promise.allSettled(
    sourceIds.map((sourceId) => brewauthAdmin.createMergeJob(sourceId, baseId)),
  );
  const started: MergeProfilesResult['started'] = [];
  const failed: MergeProfilesResult['failed'] = [];
  results.forEach((result, i) => {
    const sourceId = sourceIds[i];
    if (result.status === 'fulfilled') {
      started.push({ sourceId, job_id: result.value.job_id });
    } else {
      const { reason } = result;
      const error = reason instanceof Error && reason.message ? reason.message : 'Merge failed';
      failed.push({ sourceId, error });
    }
  });
  return { started, failed };
}

// Groups are brewauth's (jorigin/brewauth#29, #120 P3-6): every group read
// and write goes through `@/api/brewauthGroups`. Java's single-group read is
// gone; the only Java group path left is the avatar (`groupAvatarPath`).
